import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import CopyComponent from 'components/field/CopyComponent';
import EnumSelect from 'components/field/EnumSelect';
import ProgressWizard from 'components/field/ProgressWizard';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import { MobileWizardContainer } from 'components/field/styled/ProgressWizard.styled';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import { FormFooter } from 'components/form/FormLayout.styled';
import Alert from 'components/global/Alert';
import Authorization from 'components/global/Authorization';
import FormHeader from 'components/global/FormHeader';
import Image from 'components/global/Image';
import ModuleToolTipContainer from 'components/global/ModuleToolTipContainer';
import Typography from 'components/global/Typography';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMediaQuery from 'hooks/useMediaQuery';
import { usePrompt } from 'hooks/usePrompt';
import useRestriction, { SYSTEM_LIMITS } from 'hooks/useRestriction';

import BrandForm from 'pages/Settings/Brands/BrandForm';

import { AUTOCOMPLETE, SHIPPING_PARTNERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import PATHS, { mergePaths } from 'utils/paths';

import {
	faAngleLeft,
	faAngleRight,
	faArrowLeft,
	faEye,
	faKeySkeletonLeftRight,
	faPlug,
	faScrewdriverWrench
} from '@fortawesome/pro-solid-svg-icons';

import { getConnectedPartnersList } from '../ConnectedPartners/ConnectedPartnersSlice.list';
import { setFormValue } from '../ShippingPartnersSlice';
import * as S from './ShippingPartnerForm.styled';

/**
 * @author Burak Ak
 */

const ShippingFormButtons = ({
	selectedIndex,
	setSelectedIndex,
	credentialForm,
	settingsForm,
	searchParams,
	promptState,
	forHeader
}) => {
	const navigate = useNavigate();
	const axiosPrivate = useAxiosPrivate();
	const dispatch = useDispatch();
	const { credentials } = useSelector((state) => state?.shippingPartners);

	const { showError, showSuccess } = useAlert();
	const { getValues: credentialGetValues } = credentialForm;
	const handleCancel = (e) => {
		e.preventDefault();
		if (promptState) {
			navigate(mergePaths(PATHS.SHIPPING_PARTNERS, PATHS.MORE_CONNECTION));
		} else {
			navigate(-1);
		}
	};

	const onSubmit = async (data) => {
		try {
			const requestJson = {
				credentials: JSON.stringify(credentials),
				settings: { ...data, webhookURL: data.webhookURL },
				deliveryCompanyID: searchParams.get('id')
			};

			const mode = searchParams.get('mode');
			let response;
			if (mode === 'edit') {
				delete requestJson.deliveryCompanyID;
				response = await axiosPrivate.post(SHIPPING_PARTNERS.UPDATE_DELIVERY_CHANNEL_SETTINGS, {
					...requestJson,
					settingsID: searchParams.get('settingsID')
				});
			} else {
				response = await axiosPrivate.post(SHIPPING_PARTNERS.ADD_DELIVERY_CHANNEL_SETTINGS, requestJson);
			}

			if (response.status === 200) {
				await dispatch(getConnectedPartnersList({ invalidate: true })).unwrap();
				dispatch(setFormValue({ settings: data }));
				showSuccess();
				navigate(mergePaths(PATHS.SHIPPING_PARTNERS, PATHS.CONNECTED_PARTNERS));
			} else {
				AppLogger.error(response);
				showError();
			}
		} catch (error) {
			if (error.response?.data.code === 'error.C020') {
				showError({
					message: 'error.connectionNameAlreadyExists'
				});
			} else if (error.response?.data.code === 'error.SP001') {
				showError({
					message: 'errorMsg.notEditableForOmniAggregator'
				});
			} else if (error.response?.data.code === 'error.F014') {
				navigate(PATHS.OTO_MODULES);
				showError({
					header: 'info.limitedShippingPartnersTitle',
					message: 'info.limitedShippingPartnersDescription'
				});
			} else {
				showError({ message: error.response?.data.code });
				AppLogger.error(error);
			}
		}
	};

	const credentialOnSubmit = () => {
		dispatch(setFormValue({ credentials: { ...credentialGetValues() } }));
		next();
	};

	const next = () => {
		setSelectedIndex(selectedIndex !== 2 ? selectedIndex + 1 : selectedIndex);
	};

	const { handleSubmit: credentialSubmit } = credentialForm;
	const { handleSubmit: settingsSubmit } = settingsForm;
	const stages = [
		{ name: 'credentials', icon: faKeySkeletonLeftRight },
		{ name: 'settings', icon: faScrewdriverWrench }
	];

	return (
		<>
			{forHeader === true ? (
				<>
					<HeaderContainerFormHeader>
						<FormHeader boldTitle="shippingPartners" icon={faArrowLeft} />
						<ProgressWizard
							clickable={false}
							className="progressWizardFullscreen"
							stages={stages}
							index={selectedIndex}
							setIndex={setSelectedIndex}
						/>

						<S.ButtonContainer>
							<Button label="cancel" variant="outline" onClick={(e) => handleCancel(e)} />

							{selectedIndex === 1 && (
								<Button
									icon={faAngleLeft}
									label="previous"
									onClick={() => {
										setSelectedIndex(selectedIndex - 1);
									}}
									variant="outline"
								/>
							)}
							{selectedIndex === 0 && (
								<Button
									icon={faAngleRight}
									iconPos="right"
									label="continue"
									onClick={credentialSubmit(credentialOnSubmit)}
								/>
							)}
							{selectedIndex === 1 && (
								<Button label="save" onClick={settingsSubmit(onSubmit)} variant="blue" />
							)}
						</S.ButtonContainer>
					</HeaderContainerFormHeader>
					<MobileWizardContainer>
						<ProgressWizard index={selectedIndex} stages={stages} setIndex={setSelectedIndex} paramMode />
					</MobileWizardContainer>
				</>
			) : (
				<S.FooterButtonContainer>
					{!searchParams.has('otoMobile') && (
						<Button label="cancel" variant="outline" onClick={(e) => handleCancel(e)} />
					)}

					{selectedIndex === 1 && (
						<Button
							icon={faAngleLeft}
							label="previous"
							onClick={() => {
								setSelectedIndex(selectedIndex - 1);
							}}
							variant="outline"
						/>
					)}
					{selectedIndex === 0 && (
						<Button
							icon={faAngleRight}
							iconPos="right"
							label="continue"
							onClick={credentialSubmit(credentialOnSubmit)}
						/>
					)}
					{selectedIndex === 1 && <Button label="save" onClick={settingsSubmit(onSubmit)} variant="blue" />}
				</S.FooterButtonContainer>
			)}
		</>
	);
};

const ShippingFormHeader = ({ form }) => {
	const [searchParams] = useSearchParams();

	const [shippingPartnerData, setShippingPartnerData] = useState({ img: null, name: null });
	const text = form === 'credentials' ? 'ecomSetup.credentialsSubHeader' : 'ecomSetup.settingsSubHeader';

	useEffect(() => {
		const img = searchParams.get('img');
		const name = searchParams.get('name');
		setShippingPartnerData({ img, name });
	}, [searchParams]);
	return (
		<>
			<S.TitleContainer style={{ width: 'fit-content' }}>
				<Image
					width={'5rem'}
					height={'5rem'}
					url={shippingPartnerData.img}
					alt={`${shippingPartnerData.name}_logo`}
				/>

				<S.TitleWrapper>
					<Typography fontWeight="500">{shippingPartnerData.name}</Typography>
				</S.TitleWrapper>
			</S.TitleContainer>

			<Alert content={text} className="m-3" showIcon />
		</>
	);
};

const Credentials = ({ data, formMethods, credentialSchema, setPromptState }) => {
	const {
		reset,
		formState: { isDirty, isSubmitSuccessful, isSubmitting },
		handleSubmit
	} = formMethods;

	const [searchParams] = useSearchParams();

	const { credentials } = useSelector((state) => state?.shippingPartners);
	const axiosPrivate = useAxiosPrivate();
	const { showInfo, showError } = useAlert();

	useState(() => {
		setPromptState(isDirty && !isSubmitSuccessful && !isSubmitting);
	}, []);
	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const createFormElems = (data, formMethods) => {
		const colSpan = { md: 6 };
		const { watch, setValue } = formMethods;
		return data.map(({ parent, fieldName, required, values }) => {
			const name = parent ? `${parent}.${fieldName}` : `${fieldName}`;
			if (values) {
				return (
					<FormItem key={fieldName} label={name} required={required} colSpan={colSpan}>
						<EnumSelect
							data={values}
							value={watch(name)}
							setValue={(value) => {
								setValue(name, value, { shouldValidate: true, shouldDirty: true });
							}}
							name={name}
						/>
					</FormItem>
				);
			} else {
				return (
					<FormItem key={fieldName} label={fieldName} required={required} colSpan={colSpan}>
						<StringInput name={name} maxlength="750" />
					</FormItem>
				);
			}
		});
	};

	useEffect(() => {
		reset({ ...credentialSchema.defaultValues, ...credentials });
	}, [credentialSchema]);

	const testConnection = async (data) => {
		try {
			const requestJson = {
				...data,
				name: searchParams.get('integrationName'),
				deliveryCompanyID: searchParams.get('id')
			};

			const response = await axiosPrivate.post(SHIPPING_PARTNERS.TEST_CONNECTION_SERVICE_DC, requestJson);

			if (response.status === 200) {
				if (response.data.status === true) {
					showInfo({
						header: 'success',
						message: 'info.DCTestConnectionSuccess'
					});
				}
			} else {
				AppLogger.error(response);
				showError();
			}
		} catch (error) {
			AppLogger.error(error);
			showError({
				header: 'error',
				message: error.response.data.code
			});
		}
	};

	return (
		<Form id="shipping-partner-credentials-form" formMethods={formMethods} borderless>
			<ShippingFormHeader form="credentials" />
			{createFormElems(data, formMethods)}

			<FormItem childStyle={{ display: 'flex', justifyContent: 'end', paddingBottom: '40px' }}>
				<Button
					icon={faPlug}
					label="testConnection"
					variant="black"
					onClick={handleSubmit(
						(e) => {
							testConnection(e);
						},
						(errors) => {
							AppLogger.log(errors);
						}
					)}
				/>
			</FormItem>
		</Form>
	);
};

const Settings = ({ formMethods, setPromptState, webhookAvailable }) => {
	const { t } = useTranslation();
	const isAuthorized = useAccessControl();
	const { track } = useAppTracking();
	const navigate = useNavigate();
	const isRestricted = useRestriction();

	const {
		watch,
		setValue,
		reset,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	const [searchParams] = useSearchParams();
	useState(() => {
		setPromptState(isDirty && !isSubmitSuccessful && !isSubmitting);
	}, []);
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);
	// Check webhookAvailable data. if its not available then do not show webhook input
	useEffect(() => {
		let token = '';
		const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
		const charactersLength = characters.length;
		for (let i = 0; i < 20; i += 1) {
			const randomValue = new Uint32Array(1);
			window.crypto.getRandomValues(randomValue);
			token += characters.charAt(randomValue[0] % charactersLength);
		}
		const integrationName = searchParams.get('name').replace(/\s/g, '');
		let webhookUrl = `https://login.tryoto.com/shipmentStatus?token=${token}&dc=${integrationName}`;
		if (integrationName === 'see') {
			webhookUrl += '&resp=dsmn23f0h8h834hg34gh3g3g';
		}
		if (integrationName) {
			setValue('webhookURL', webhookUrl);
		}
	}, []);

	const brandNameAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	const colSpan = {
		md: 6
	};

	useEffect(() => {
		if (!isDirty) {
			reset({ ...watch() });
		}
	}, [isDirty]);

	return (
		<Form id="shipping-partner-settings-form" formMethods={formMethods} borderless>
			<ShippingFormHeader form="settings" />

			<FormItem label="connectionName" colSpan={colSpan} required>
				<StringInput name="name" />
			</FormItem>

			<FormItem label="serviceType" colSpan={colSpan} required>
				<EnumSelect
					searchable={false}
					name="serviceType"
					enumName="PriceConfigurationServiceType"
					setValue={(value) => {
						setValue(`serviceType`, value, { shouldValidate: true, shouldDirty: false });
					}}
					value={watch(`serviceType`)}
					placeholder="serviceTypePlaceHolder"
				/>
			</FormItem>

			<FormItem label="active" colSpan={colSpan}>
				<SwitchInput className={'m-3'} name="active" />
			</FormItem>

			<FormItem label="donotsendSMS" colSpan={colSpan} toolTip={t('tooltip.donotSendSMS')}>
				<SwitchInput className={'m-3'} name="donotsendSMS" />
			</FormItem>

			<FormItem label="returnAvailable" colSpan={colSpan} toolTip={t('tooltip.returnAvailable')}>
				<SwitchInput className={'m-3'} name="returnAvailable" />
			</FormItem>

			{webhookAvailable === 1 && (
				<FormItem label="webhookUrl" colSpan={colSpan} toolTip={t('tooltip.webhookUrl')}>
					<CopyComponent value={watch('webhookURL')} />
				</FormItem>
			)}

			<S.AccordionFullWidth
				header="advancedSettings"
				className="m-3"
				contentStyle={{ display: 'flex', flexWrap: 'wrap' }}>
				{/* <FormItem label="hidePhoneNumber" colSpan={colSpan}>
					<SwitchInput className={'m-3'} name="hidePhoneNumber" />
				</FormItem> */}
				<FormItem label="volumetricWeightRate" toolTip="tooltip.volumetricWeightRate" colSpan={colSpan}>
					<EnumSelect
						data={['3000', '4000', '5000', '6000']}
						value={watch('volumetricWeightRate')}
						setValue={(value) => {
							setValue(`volumetricWeightRate`, value, { shouldValidate: true, shouldDirty: false });
						}}
						searchable={false}
					/>
				</FormItem>
				{/* <FormItem label="hideItems" colSpan={colSpan}>
					<SwitchInput className={'m-3'} name="hideItems" />
				</FormItem> */}
				<FormItem label="storeName" colSpan={colSpan} isLoading={isLoading} roleName="BrandingModule">
					<AutoComplete
						name="storeID"
						autoCompleteName={AUTOCOMPLETE.BRANDS}
						placeholder={t('partnerStoreNamePlaceHolder')}
						value={watch('storeID')}
						setValue={(value) => {
							setValue(`storeID`, value?.ID ?? null, { shouldValidate: true, shouldDirty: true });
						}}
						firstItem={
							isAuthorized('/autocomplete/brand/addANewBrand') && {
								action: () =>
									NiceModal.show(BrandForm, {
										setBrand: (value) => {
											setValue(`storeID`, value, {
												shouldValidate: true,
												shouldDirty: true
											});
										}
									}),
								label: <Typography color="var(--blue)">addANewBrand</Typography>,
								disabled: (dataSize) => isRestricted(SYSTEM_LIMITS.BRAND, dataSize),
								disabledTooltip: (
									<div style={{ display: 'block' }}>
										<ModuleToolTipContainer
											infoImage="lottie_multiBrand"
											title="multiBrandModule"
											description="multiBrandModuleSubtitle"
										/>

										<Button
											style={{ marginLeft: 'Auto', marginRight: 'Auto' }}
											className="mt-3"
											label="showPlans"
											icon={faEye}
											variant="blue"
											onMouseDown={() => {
												track('moreThan3Brands');
												navigate(PATHS.OTO_MODULES);
											}}
										/>
									</div>
								)
							}
						}
						columns={brandNameAutocompleteColumns}
						selectionLabelAccessor={['Name']}
					/>
				</FormItem>
			</S.AccordionFullWidth>

			<Authorization path="ShippingPartnerFormAdvanceSettings">
				<S.AccordionFullWidth
					header="developerSettings"
					className="m-3"
					contentStyle={{ display: 'flex', flexWrap: 'wrap' }}>
					<FormItem label="omniAggregator" colSpan={colSpan}>
						<SwitchInput className={'m-3'} name="omniAggregator" />
					</FormItem>

					<FormItem label="pickupAllCities" colSpan={colSpan}>
						<SwitchInput className={'m-3'} name="pickupAllCities" />
					</FormItem>

					<FormItem label="pickupRequestSchedule" colSpan={colSpan}>
						<SwitchInput className={'m-3'} name="pickupSchedule" />
					</FormItem>
				</S.AccordionFullWidth>
			</Authorization>
		</Form>
	);
};

const ShippingPartnerForm = () => {
	const axiosPrivate = useAxiosPrivate();
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [searchParams] = useSearchParams();
	const [formTemplate, setFormTemplate] = useState([]);
	const [credentialSchema, setCredentialSchema] = useState({ schema: {}, defaultValues: {} });
	const [promptState, setPromptState] = useState();
	const [webhookAvailable, setWebhookAvailable] = useState(0);

	const mobileView = useMediaQuery(Breakpoints.tablet);

	const dispatch = useDispatch();
	useEffect(() => {
		dispatch(setFormValue({ credentials: {}, settings: {} }));
	}, []);

	const getFieldSchema = (required, value) => {
		if (typeof value === 'number') {
			return required ? yup.number().nullable().required() : yup.number().nullable();
		} else {
			return required ? yup.string().nullable().required() : yup.string().nullable();
		}
	};

	const createFormSchema = (data) => {
		const schema = {};
		const parents = {};
		const defVal = {};

		data.forEach(({ parent, fieldName, default: def, required }) => {
			if (parent) {
				if (!defVal[parent]) {
					defVal[parent] = {};
					parents[parent] = { shape: {} };
				}
				parents[parent].shape[fieldName] = getFieldSchema(required);
				defVal[parent][fieldName] = def ?? null;
			} else {
				schema[fieldName] = getFieldSchema(required);
				defVal[fieldName] = def ?? null;
			}
		});

		for (const i in parents) {
			schema[i] = yup.object().shape({ ...parents[i].shape });
		}
		return { defaultValues: defVal, schema: yup.object().shape({ ...schema }) };
	};

	const { schema, defaultValues } = credentialSchema;
	const formMethodsCredentials = useForm({
		resolver: yupResolver(schema),
		defaultValues
	});

	const getDeliveryCompanyData = async () => {
		const mode = searchParams.get('mode');

		let reqBody, url;

		if (mode === 'edit') {
			reqBody = { settingsID: searchParams.get('settingsID') };
			url = SHIPPING_PARTNERS.GET_DELIVERY_COMPANY_FORM_SETTINGS;
		} else {
			reqBody = { companyID: searchParams.get('id') };
			url = SHIPPING_PARTNERS.GET_DELIVERY_COMPANY_FORM;
		}

		const { data = {} } = await axiosPrivate.post(url, reqBody);

		setFormTemplate(data.credentials ?? []);
		setWebhookAvailable(data.webhookAvailable ?? 0);
		const credentialSchema = createFormSchema(data.credentials ?? []);
		setCredentialSchema(credentialSchema);

		if (mode === 'edit') {
			formMethodsSettings.reset({
				deliverySLA: data.settings.deliverySLA ?? null,
				pickupSLA: data.settings.pickupSLA ?? null,
				name: data.settings.name ?? null,
				webhookURL: data.settings.webhookURL ?? null,
				donotsendSMS: data.settings.donotsendSMS ?? false,
				returnAvailable: data.settings.returnAvailable ?? false,
				active: data.settings.active ?? false,
				serviceType: data.settings.serviceType ?? null,
				volumetricWeightRate: data.settings.volumetricWeightRate ?? null,
				storeID: data.settings.storeID ?? null,
				omniAggregator: data.settings.omniAggregator ?? false,
				pickupAllCities: data.settings.pickupAllCities ?? true,
				pickupSchedule: data.settings.pickupSchedule ?? false,
				hidePhoneNumber: data.settings.hidePhoneNumber ?? false,
				hideItems: data.settings.hideItems ?? false
			});

			dispatch(
				setFormValue({ credentials: { ...credentialSchema.defaultValues }, settings: { ...data.settings } })
			);
		}
	};

	useEffect(() => {
		(async () => {
			await getDeliveryCompanyData();
		})();
	}, [searchParams]);

	const formMethodsSettings = useForm({
		resolver: yupResolver(
			yup.object().shape({
				deliverySLA: yup.string().nullable(),
				pickupSLA: yup.string().nullable(),
				name: yup.string().nullable().required(),
				webhookURL: yup.string().nullable(),
				donotsendSMS: yup.boolean(),
				returnAvailable: yup.boolean(),
				hidePhoneNumber: yup.boolean(),
				hideItems: yup.boolean(),
				active: yup.boolean(),
				serviceType: yup.string().nullable().required(),
				volumetricWeightRate: yup.string().nullable(),
				omniAggregator: yup.boolean(),
				pickupAllCities: yup.boolean(),
				pickupSchedule: yup.boolean()
			})
		),
		defaultValues: {
			deliverySLA: null,
			pickupSLA: null,
			name: null,
			webhookURL: null,
			donotsendSMS: false,
			returnAvailable: false,
			hidePhoneNumber: false,
			volumetricWeightRate: null,
			hideItems: false,
			active: false,
			serviceType: null,
			omniAggregator: false,
			pickupAllCities: true,
			pickupSchedule: false
		}
	});

	const wizardArr = [
		<Credentials
			key="1"
			data={formTemplate}
			formMethods={formMethodsCredentials}
			credentialSchema={credentialSchema}
			setPromptState={setPromptState}
			promptState={promptState}
		/>,
		<Settings
			key="2"
			formMethods={formMethodsSettings}
			setPromptState={setPromptState}
			promptState={promptState}
			webhookAvailable={webhookAvailable}
		/>
	];

	return (
		<div>
			<ShippingFormButtons
				forHeader={true}
				credentialForm={formMethodsCredentials}
				settingsForm={formMethodsSettings}
				setSelectedIndex={setSelectedIndex}
				selectedIndex={selectedIndex}
				data={formTemplate}
				searchParams={searchParams}
				promptState={promptState}
				mobileView={mobileView}
			/>

			<S.FormContainer>{wizardArr[selectedIndex]}</S.FormContainer>

			<FormFooter>
				<ShippingFormButtons
					forHeader={false}
					credentialForm={formMethodsCredentials}
					settingsForm={formMethodsSettings}
					setSelectedIndex={setSelectedIndex}
					selectedIndex={selectedIndex}
					data={formTemplate}
					searchParams={searchParams}
					promptState={promptState}
					mobileView={mobileView}
				/>
			</FormFooter>
		</div>
	);
};

export default ShippingPartnerForm;
